import React from 'react';
import { node, oneOf, oneOfType, string } from 'prop-types';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { border, color, sizing, spacing } from '@/components/styled-system';

/**
 * function to get column width
 * @param {number|string} size
 * @returns
 */
const getColWidth = (size) => {
  if (Number.isInteger(size) && size >= 1 && size <= 12) {
    return `calc((${size} / 12) * 100%)`;
  }
  if (size === 'auto') {
    return 'auto';
  }
  return '100%';
};

const StyledCol = styled.div`
  position: relative;
  box-sizing: border-box;
  ${({ size, minSize, maxSize }) => css`
    width: ${getColWidth(size)};
    ${maxSize &&
    css`
      flex: 0 0 ${getColWidth(maxSize)};
      max-width: ${getColWidth(maxSize)};
    `}
    ${minSize &&
    css`
      flex: 0 0 ${getColWidth(minSize)};
      min-width: ${getColWidth(minSize)};
    `}
  `}
  ${sizing};
  ${spacing};
  ${color};
  ${border};
`;

/**
 * Col Component
 * @typedef {{
 * children: React.ReactNode,
 * size: 1|2|3|4|5|6|7|8|9|10|11|12|'auto',
 * maxSize: 1|2|3|4|5|6|7|8|9|10|11|12|'auto',
 * minSize: 1|2|3|4|5|6|7|8|9|10|11|12|'auto'}
 * & import('../../styled-system').BorderProps
 * & import('../../styled-system').ColorProps
 * & import('../../styled-system').SizingProps
 * & import('../../styled-system').SpacingProps} ColProps
 * @type {React.FC<ColProps>}
 * @returns
 */
const Col = ({ children, ...props }) => <StyledCol {...props}>{children}</StyledCol>;

Col.propTypes = {
  children: oneOfType([string, node]),
  size: oneOf([null, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 'auto']),
  maxSize: oneOf([null, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 'auto']),
  minSize: oneOf([null, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 'auto']),
};

Col.defaultProps = {
  children: null,
  size: null,
  maxSize: null,
  minSize: null,
};

export default Col;
